// Test & Refine redesign - running state.
//
// The real run is a single async call, so per-model timing is presentational:
// a local timer advances the display one model at a time while the parent
// keeps isSimulating true. We deliberately show status only (queued /
// scoring / scored), NOT invented per-model before-to-after numbers, since
// those are not known until the run returns. The one honest headline is
// "N / M models scored". Respects prefers-reduced-motion (jumps to done).
import { useEffect, useRef, useState } from "react";
import { Check } from "lucide-react";
import { getModelDisplayName } from "@/app/constants/models";
import { TrOrb } from "./shared";

const STEP_MS = 900;

const STEPS = [
  "Reading your queued actions and monitoring baseline",
  "Querying each model for the current answer",
  "Applying your actions and re-querying",
  "Scoring the before to after shift",
];

export function RunningView({
  query,
  modelIds,
  actionsApplied,
}: {
  query: string;
  modelIds: string[];
  actionsApplied: number;
}) {
  const total = Math.max(1, modelIds.length);
  const [step, setStep] = useState(0);
  const timer = useRef<number | null>(null);

  useEffect(() => {
    const reduce =
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (reduce) {
      setStep(total);
      return;
    }

    timer.current = window.setInterval(() => {
      setStep((s) => {
        if (s >= total) {
          if (timer.current) window.clearInterval(timer.current);
          return total;
        }
        return s + 1;
      });
    }, STEP_MS);

    return () => {
      if (timer.current) window.clearInterval(timer.current);
    };
  }, [total]);

  const done = Math.min(step, total);
  const pct = Math.round((done / total) * 100);
  const displayQuery = query.trim() || "your query";

  return (
    <div
      style={{
        position: "relative",
        overflow: "hidden",
        border: "1px solid var(--spectral-violet-20)",
        borderRadius: 16,
        boxShadow: "var(--elevation-md)",
        background:
          "radial-gradient(120% 140% at 8% 0%, var(--spectral-violet-10), transparent 46%), radial-gradient(120% 140% at 100% 100%, var(--alpha-teal-10), transparent 42%), var(--card)",
      }}
    >
      {/* hero */}
      <div
        style={{
          padding: "34px 28px 22px",
          textAlign: "center",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <div style={{ marginBottom: 16 }}>
          <TrOrb size={52} radius={15} iconSize={25} />
        </div>
        <div style={{ fontSize: 20, fontWeight: 600, letterSpacing: "-.01em", color: "var(--foreground)", marginBottom: 5 }}>
          Running your simulation
        </div>
        <div style={{ fontSize: 13, color: "var(--muted-foreground)" }}>
          Testing{" "}
          <strong style={{ color: "var(--foreground)", fontWeight: 600 }}>"{displayQuery}"</strong> with{" "}
          <strong style={{ color: "var(--foreground)", fontWeight: 600 }}>
            {actionsApplied} {actionsApplied === 1 ? "action" : "actions"} applied
          </strong>
        </div>
      </div>

      {/* models scored (the one honest counter) */}
      <div style={{ display: "flex", justifyContent: "center", padding: "2px 28px 22px" }}>
        <div style={{ textAlign: "center" }}>
          <div
            style={{
              fontSize: 30,
              fontWeight: 800,
              color: "var(--foreground)",
              fontVariantNumeric: "tabular-nums",
              letterSpacing: "-.02em",
              lineHeight: 1,
            }}
          >
            {done} / {total}
          </div>
          <div style={{ fontSize: 11.5, color: "var(--muted-foreground)", marginTop: 6 }}>models scored</div>
        </div>
      </div>

      {/* active step line */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 9, padding: "0 28px 16px" }}>
        <span
          className="tr-spin"
          style={{
            width: 13,
            height: 13,
            border: "2px solid var(--spectral-violet-20)",
            borderTopColor: "var(--spectral-violet)",
            borderRadius: 9999,
            flexShrink: 0,
          }}
        />
        <span style={{ fontSize: 13, color: "var(--foreground)", fontWeight: 500 }}>
          {STEPS[Math.min(step, STEPS.length - 1)]}
        </span>
      </div>

      {/* per-model resolving rows (status only) */}
      <div style={{ borderTop: "1px solid var(--border)", padding: "8px 0" }}>
        {modelIds.map((id, i) => {
          const isDone = i < step;
          const isActive = i === step;
          return (
            <div
              key={id}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                padding: "11px 24px",
                opacity: i <= step ? 1 : 0.42,
                transition: "opacity .4s",
              }}
            >
              {isDone ? (
                <span
                  style={{
                    width: 20,
                    height: 20,
                    borderRadius: 9999,
                    background: "var(--success-bg)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "var(--signal-green)",
                    flexShrink: 0,
                  }}
                >
                  <Check size={12} strokeWidth={2.6} />
                </span>
              ) : isActive ? (
                <span
                  className="tr-spin"
                  style={{
                    width: 14,
                    height: 14,
                    border: "2px solid var(--alpha-teal-20)",
                    borderTopColor: "var(--alpha-teal)",
                    borderRadius: 9999,
                    flexShrink: 0,
                    margin: "0 3px",
                  }}
                />
              ) : (
                <span
                  style={{
                    width: 14,
                    height: 14,
                    borderRadius: 9999,
                    border: "2px solid var(--silver-mist)",
                    flexShrink: 0,
                    margin: "0 3px",
                  }}
                />
              )}
              <span
                style={{
                  flex: 1,
                  minWidth: 0,
                  fontSize: 14,
                  fontWeight: 600,
                  color: isDone ? "var(--foreground)" : "var(--muted-foreground)",
                }}
              >
                {getModelDisplayName(id)}
              </span>
              <span style={{ fontSize: 12, color: isDone ? "var(--signal-green)" : "var(--muted-foreground)", fontWeight: isDone ? 700 : 400 }}>
                {isDone ? "scored" : isActive ? "scoring…" : "queued"}
              </span>
            </div>
          );
        })}
      </div>

      {/* progress track */}
      <div style={{ borderTop: "1px solid var(--border)", padding: "16px 24px", display: "flex", alignItems: "center", gap: 14 }}>
        <div style={{ flex: 1, height: 6, borderRadius: 9999, background: "var(--muted)", overflow: "hidden" }}>
          <div
            style={{
              height: "100%",
              width: `${pct}%`,
              borderRadius: 9999,
              background: "var(--orb-gradient)",
              transition: "width .8s var(--ease-standard)",
            }}
          />
        </div>
        <span style={{ fontSize: 11.5, fontWeight: 600, color: "var(--muted-foreground)", whiteSpace: "nowrap" }}>
          Won't affect your live monitoring
        </span>
      </div>
    </div>
  );
}
